import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
    Title,
} from 'chart.js';
import { cachedGet } from './apiCache.js';
import { parseISO, format } from 'date-fns';
import { handleUIEvent } from './eventLogger.js';
import { useTagFilter } from "./TagFilterContext.js";
import { useDateRange } from "./dateRangeContext.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenLayoutStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const MODULE_NAME = "_5_profit_chart";

const mkSpikesPlugin = {
    id: 'mkSpikesPlugin',
    afterDatasetsDraw: (chart) => {
        if (!chart || !chart.data || !chart.data.datasets || !chart.data.datasets[0]) return;
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;

        const dataset = chart.data.datasets[0];
        const profits = dataset.data;
        const labels = chart.data.labels;

        if (!profits || !labels) return;

        profits.forEach((profit, i) => {
            if (profit < -1000) {
                const x = scales.x.getPixelForValue(labels[i]);
                const y = scales.y.getPixelForValue(profit);
                const spikeWidth = 12;
                const spikeHeight = 24;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, y + 4);
                ctx.lineTo(x - spikeWidth / 2, y + spikeHeight);
                ctx.lineTo(x + spikeWidth / 2, y + spikeHeight);
                ctx.closePath();
                ctx.fillStyle = '#b71c1c';
                ctx.shadowColor = '#222';
                ctx.shadowBlur = 6;
                ctx.fill();
                ctx.restore();
            }
        });
    }
};

function TooltipIcon({ text }) {
    if (!text) return null;
    return (
        <span
            style={{
                marginLeft: 8,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888",
                fontSize: 18
            }}
            title={text}
            tabIndex={0}
            role="button"
            aria-label="tooltip"
            onClick={e => handleUIEvent(
                e,
                { name: "tooltip", label: "Profit Chart Tooltip" },
                null,
                "profit_chart_screen",
                MODULE_NAME
            )}
        >
            🛈
        </span>
    );
}

const RANGE_OPTIONS = [
    { label: "Last 30 Days", value: "30" },
    { label: "Last 60 Days", value: "60" },
    { label: "Last 90 Days", value: "90" },
    { label: "Last 180 Days", value: "180" },
    { label: "ALL", value: "ALL" }
];

function groupByWeek(data, date_col) {
    const weekMap = new Map();
    data.forEach(item => {
        const dateObj = parseISO(item[date_col]);
        const year = dateObj.getFullYear();
        const week = format(dateObj, "I");
        const key = `${year}-W${week}`;
        if (!weekMap.has(key)) {
            weekMap.set(key, { realized_profit: 0, cumulative_profit: 0, lastDate: dateObj });
        }
        weekMap.get(key).realized_profit += item.realized_profit;
        weekMap.get(key).cumulative_profit = item.cumulative_profit;
        weekMap.get(key).lastDate = dateObj;
    });
    return Array.from(weekMap.entries())
        .sort((a, b) => a[1].lastDate - b[1].lastDate)
        .map(([key, val]) => ({
            weekLabel: format(val.lastDate, "MMM dd, yyyy"),
            realized_profit: val.realized_profit,
            cumulative_profit: val.cumulative_profit
        }));
}

// --- Mobile detection ---
function useIsMobile(breakpoint = 700) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);
    return isMobile;
}

const ProfitChart = (props) => {
    const { globalTagFilter } = useTagFilter();
    const { windowDays, setWindowDays } = useDateRange();
    const [chartData, setChartData] = useState([]);

    const screen = props.screen || "profit_chart_screen";
    const isMobile = useIsMobile();

    // Check if in fullscreen mode
    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    const [loading, setLoading] = useState(true);
    const [tooltipText, setTooltipText] = useState('');
    const [error, setError] = useState("");
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        if (!isVisible) return;

        let isMounted = true;
        const fetchChartData = async () => {
            setLoading(true);
            try {
                const params = {
                    range: windowDays
                };
        
                if (globalTagFilter) {
                    params.tag_filter = globalTagFilter;
                }

                const res = await cachedGet(`${process.env.REACT_APP_API_URL}/api/chart_profit`, params);
                const { data, date_col, tooltip } = res.data;

                let labels, profits, cumulativeProfits;

                // --- For mobile, always use daily data for more detail ---
                if (windowDays === "ALL" && !isMobile) {
                    const weeklyData = groupByWeek(data, date_col);
                    labels = weeklyData.map(item => item.weekLabel);
                    profits = weeklyData.map(item => item.realized_profit);
                    cumulativeProfits = weeklyData.map(item => item.cumulative_profit);
                } else {
                    const years = Array.from(new Set(data.map(item => parseISO(item[date_col]).getFullYear())));
                    const showYear = years.length > 1;
                    labels = data.map(item => {
                        const dateObj = parseISO(item[date_col]);
                        // Use MM/DD/YY for mobile, otherwise keep existing
                        return isMobile
                            ? format(dateObj, 'MM/dd/yy')
                            : (showYear
                                ? format(dateObj, 'MMM dd, yyyy')
                                : format(dateObj, 'MMM dd'));
                    });
                    profits = data.map(item => item.realized_profit);
                    cumulativeProfits = data.map(item => item.cumulative_profit);
                }

                if (isMounted) {
                    setChartData({
                        labels: labels,
                        datasets: [
                            {
                                label: windowDays === "ALL"
                                    ? (isMobile ? "Daily Realized Profit (ALL)" : "Weekly Realized Profit (ALL)")
                                    : `Realized Profit (${RANGE_OPTIONS.find(opt => opt.value === windowDays)?.label || "Last 30 Days"})`,
                                data: profits,
                                borderColor: '#1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                fill: true,
                                tension: 0.3,
                                pointRadius: isMobile ? 3 : 2,
                            },
                            {
                                label: 'Cumulative Profit',
                                data: cumulativeProfits,
                                borderColor: '#43a047',
                                backgroundColor: 'rgba(67, 160, 71, 0.0)',
                                fill: false,
                                tension: 0,
                                pointRadius: 0,
                                borderWidth: 2,
                            }
                        ],
                    });
                    setTooltipText(tooltip || '');
                    setError("");
                }
            } catch (err) {
                if (isMounted) {
                    setError('Error fetching profit chart data');
                    handleUIEvent(
                        { type: "error" },
                        { name: "profit_chart_error", message: err?.message || String(err) },
                        null,
                        screen,
                        MODULE_NAME
                    );
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchChartData();
        return () => { isMounted = false; };
    }, [windowDays, globalTagFilter, screen, isMobile, isVisible]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: 'top' },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`;
                    }
                }
            },
            title: {
                display: false
            }
        },
        scales: {
            x: { title: { display: true, text: 'Date' } },
            y: { title: { display: true, text: 'Profit ($)' }, beginAtZero: true }
        }
    };

    if (!isVisible) return null;

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`} style={getFullscreenContainerStyles(isFullscreen)}>
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4
                    className="collapse-toggle-headline"
                    onClick={() => setCollapsed(!collapsed)}
                    style={{ cursor: "pointer", userSelect: "none", marginTop: 0, marginBottom: collapsed ? 0 : 16, flex: 1 }}
                >
                    💰Profit Chart
                    <TooltipIcon text={tooltipText} />
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Profit Chart"
                    />
                )}
            </div>
            {!collapsed && (
                <div style={getFullscreenLayoutStyles(isFullscreen, 0, 0)}>
                    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                        {/* Range filter buttons */}
                        <div style={{ display: "flex", gap: 8, marginBottom: 18, overflowX: "auto", paddingBottom: 4 }}>
                            {RANGE_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    className="profit-chart-range-btn"
                                    onClick={e => {
                                        setWindowDays(opt.value);
                                        handleUIEvent(
                                            e,
                                            {
                                                name: "range_button",
                                                label: opt.label,
                                                value: opt.value
                                            },
                                            null,
                                            screen,
                                            MODULE_NAME
                                        );
                                    }}
                                    style={{
                                        padding: "6px 16px",
                                        borderRadius: 4,
                                        border: windowDays === opt.value ? "2px solid #1976d2" : "1px solid #888",
                                        background: windowDays === opt.value ? "#1976d2" : "#222",
                                        color: windowDays === opt.value ? "#fff" : "#ccc",
                                        fontWeight: windowDays === opt.value ? 700 : 500,
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        flexShrink: 0
                                    }}
                                    aria-pressed={windowDays === opt.value}
                                    tabIndex={0}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ width: "100%", height: isFullscreen ? "clamp(400px, 70vh, 1000px)" : (isMobile ? 420 : 260) }}>
                            <Line data={chartData} options={options} plugins={[mkSpikesPlugin]} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfitChart;

